import React, { useState, useMemo, useEffect, useCallback, useRef } from "react";
import {
  Loader2, CheckCircle2, Clock, Zap, ChevronRight,
  ChevronLeft, Wallet, TrendingDown, AlertTriangle,
  RefreshCw, Plus, X, Lock, Tag, Pencil, Trash2,
  BookOpen, Code2, Sparkles, ArrowRight, Search,
} from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader,
  DialogTitle, DialogDescription, DialogFooter,
} from "@/Components/ui/dialog";
import { Button }    from "@/Components/ui/button";
import { Label }     from "@/Components/ui/label";
import { Badge }     from "@/Components/ui/badge";
import { Card }      from "@/Components/ui/card";
import { Input }     from "@/Components/ui/input";
import { Textarea }  from "@/Components/ui/textarea";
import {
  Select, SelectContent, SelectItem,
  SelectTrigger, SelectValue,
} from "@/Components/ui/select";
import { cn }        from "@/lib/utils";
import { getColor }  from "@/config/theme";
import { toast }     from "sonner";

import TestCardsMinimal  from "./TestCards";
import { DateTimePicker } from "@/Components/ui/date-time-picker";
import { API_BASE_URL }  from "@/config/api";

// ─── Component model (durations only — credits are fixed-per-action now) ──────

const TEST_COMPONENTS = {
  "/quick-screening":    [{ action: "NORMAL_TEST", mins: 10 }],
  "/quick-pnb":          [{ action: "NORMAL_TEST", mins: 10 }],
  "/quick-icoding":      [{ action: "CODING_TEST", mins: 10 }],
  "/screening":          [{ action: "NORMAL_TEST", mins: 30 }],
  "/communication":      [{ action: "NORMAL_TEST", mins: 30 }],
  "/personality-test":   [{ action: "NORMAL_TEST", mins: 30 }],
  "/behavioral-test":    [{ action: "NORMAL_TEST", mins: 30 }],
  "/all-test":           [{ action: "NORMAL_TEST", mins: 30 }],
  "/aptitude":           [{ action: "NORMAL_TEST", mins: 30 }],
  "/icoding":            [{ action: "CODING_TEST", mins: 30 }],
  "/realtime-screening": [{ action: "NORMAL_TEST", mins: 30 }],
  "/realtime-pnb":       [{ action: "NORMAL_TEST", mins: 30 }],
  "/realtime-communication": [{ action: "NORMAL_TEST", mins: 30 }],
  "flow:sc":    [{ action: "NORMAL_TEST", mins: 30 }, { action: "CODING_TEST", mins: 30 }],
  "flow:spb":   [{ action: "NORMAL_TEST", mins: 30 }, { action: "NORMAL_TEST", mins: 30 }],
  "flow:scp":   [{ action: "NORMAL_TEST", mins: 30 }, { action: "CODING_TEST", mins: 30 }, { action: "NORMAL_TEST", mins: 30 }],
  "flow:spc20": [{ action: "NORMAL_TEST", mins: 20 }, { action: "NORMAL_TEST", mins: 20 }, { action: "CODING_TEST", mins: 20 }],
  "flow:fscc": [
    { action: "NORMAL_TEST", mins: 30 }, // Screening
    { action: "NORMAL_TEST", mins: 30 }, // Communication
    { action: "CODING_TEST", mins: 30 }, // Coding
  ],
};

function getComponents(tt)   { return TEST_COMPONENTS[tt] ?? [{ action: "NORMAL_TEST", mins: 30 }]; }
function getTotalMinutes(tt) { return getComponents(tt).reduce((s, c) => s + c.mins, 0); }
function containsCoding(tt)  { return getComponents(tt).some((c) => c.action === "CODING_TEST"); }
function containsScreening(tt) {
  const id = String(tt || "").toLowerCase();
  return id.includes("screening") || id.startsWith("flow:");
}

// ─── Fixed-per-action credit model (mirrors the backend cutover) ──────────────
// Each test costs a FIXED number of credits per candidate; a flow costs 1 credit
// per component test. Actual per-plan values come from /api/entitlements/me
// (test_costs); the constants below are the safe fallback defaults.
const ACTION_BY_TEST = {
  "/realtime-screening": "REALTIME_INTERVIEW",
  "/realtime-communication": "REALTIME_INTERVIEW",
  "/realtime-pnb": "REALTIME_INTERVIEW",
  "/quick-screening": "SCREENING_TEST",
  "/screening": "SCREENING_TEST",
  "/quick-pnb": "SCREENING_TEST",
  "/personality-test": "SCREENING_TEST",
  "/behavioral-test": "SCREENING_TEST",
  "/communication": "SCREENING_TEST",
  "/all-test": "SCREENING_TEST",
  "/quick-icoding": "CODING_TEST",
  "/icoding": "CODING_TEST",
  "/aptitude": "APTITUDE_TEST",
};
const FLOW_TEST_COUNT = { "flow:sc": 2, "flow:spb": 2, "flow:scp": 3, "flow:spc20": 3, "flow:fscc": 3 };
const CANON_TEST_COSTS = {
  SCREENING_TEST: 1, REALTIME_INTERVIEW: 4, CODING_TEST: 1,
  APTITUDE_TEST: 2, APTITUDE_TEMPLATE: 1, FLOW_TEST: 1,
};

// Aptitude cost depends on the chosen source: a company Builder template
// ('template') runs at 1/candidate; AI-generated ('ai'/'mixed') at 2/candidate.
function aptitudeCr(testCosts, sourceMode) {
  const c = testCosts || CANON_TEST_COSTS;
  return String(sourceMode || 'ai') === 'template'
    ? (c.APTITUDE_TEMPLATE ?? CANON_TEST_COSTS.APTITUDE_TEMPLATE)
    : (c.APTITUDE_TEST ?? CANON_TEST_COSTS.APTITUDE_TEST);
}

// Which gateable plan feature(s) a test type needs (mirrors the backend maps).
const FEATURE_BY_TEST = {
  "/realtime-screening": "realtime_interview",
  "/realtime-communication": "realtime_interview",
  "/realtime-pnb": "realtime_interview",
  "/quick-screening": "screening_test",
  "/screening": "screening_test",
  "/quick-pnb": "screening_test",
  "/personality-test": "screening_test",
  "/behavioral-test": "screening_test",
  "/communication": "screening_test",
  "/all-test": "screening_test",
  "/quick-icoding": "coding_test",
  "/icoding": "coding_test",
  "/aptitude": "aptitude_test",
};
const FLOW_FEATURES = {
  "flow:sc": ["screening_test", "coding_test"],
  "flow:spb": ["screening_test"],
  "flow:scp": ["screening_test", "coding_test"],
  "flow:spc20": ["screening_test", "coding_test"],
  "flow:fscc": ["screening_test", "coding_test"],
};

// Fixed credits for one scheduled test of this type, for `n` candidates.
function testFixedCr(tt, testCosts, n = 1) {
  const costs = testCosts || CANON_TEST_COSTS;
  const id = String(tt || "");
  if (id.startsWith("flow:")) {
    const per = costs.FLOW_TEST ?? CANON_TEST_COSTS.FLOW_TEST;
    return per * (FLOW_TEST_COUNT[id] ?? 1) * n;
  }
  const act = ACTION_BY_TEST[id];
  const c = act ? (costs[act] ?? CANON_TEST_COSTS[act] ?? 1) : 1;
  return c * n;
}
// The plan feature(s) a test needs; [] = ungated.
function featuresForTest(tt) {
  const id = String(tt || "");
  if (id.startsWith("flow:")) return FLOW_FEATURES[id] || ["screening_test"];
  const f = FEATURE_BY_TEST[id];
  return f ? [f] : [];
}
function fmtCr(n) { return Number.isInteger(n) ? String(n) : n.toFixed(1); }

// ─── Display helpers ──────────────────────────────────────────────────────────

const DISPLAY_NAMES = {
  "/screening":          "Screening",
  "/icoding":            "Coding Challenge",
  "/personality-test":   "Personality Assessment",
  "/behavioral-test":    "Behavioral Assessment",
  "/all-test":           "Personality & Behavioral",
  "/communication":      "Communication Test",
  "/aptitude":           "Aptitude Test",
  "/quick-screening":    "Quick Screening",
  "/quick-icoding":      "Quick Coding",
  "/quick-pnb":          "Quick P&B",
  "/realtime-screening": "Realtime Screening (Live)",
  "/realtime-pnb":       "Realtime P&B (Live)",
  "/realtime-communication": "Realtime Communication (Live)",
  "flow:sc":    "Screening + Coding",
  "flow:spb":   "Screening + P&B",
  "flow:scp":   "Screening + Coding + P&B",
  "flow:spc20": "Screening + P&B + Coding (20/20/20)",
  "flow:fscc": "Screening + Communication + Coding",
};
function displayName(id) { return DISPLAY_NAMES[id] ?? id; }

function testCategory(id) {
  const t = String(id ?? "").toLowerCase();
  if (t.includes("coding") || t.includes("icoding")) return "coding";
  if (t.includes("personality") || t.includes("all-test") || t.includes("pnb")) return "personality";
  if (t.includes("behavioral")) return "behavioral";
  if (t.includes("communication")) return "communication";
  if (t.includes("aptitude")) return "aptitude";
  if (t.includes("screening")) return "screening";
  if (t.startsWith("flow:")) return "flow";
  return "other";
}
const SORT_ORDER = { screening:1, aptitude:2, coding:3, personality:4, behavioral:5, communication:6, flow:7 };
function sortTests(cards) {
  return [...cards].sort((a,b) => (SORT_ORDER[testCategory(a)]??99)-(SORT_ORDER[testCategory(b)]??99));
}

// ─── Date helpers ─────────────────────────────────────────────────────────────

function toInputValue(d) {
  if (!d) return "";
  try {
    const dt = new Date(d);
    if (isNaN(dt.getTime())) return "";
    return `${dt.getFullYear()}-${String(dt.getMonth()+1).padStart(2,"0")}-${String(dt.getDate()).padStart(2,"0")}T${String(dt.getHours()).padStart(2,"0")}:${String(dt.getMinutes()).padStart(2,"0")}`;
  } catch { return d; }
}
function fmtDateTime(dt) {
  if (!dt) return "—";
  const d = new Date(dt);
  return d.toLocaleDateString("en-US",{month:"short",day:"numeric"})+" "+
         d.toLocaleTimeString("en-US",{hour:"numeric",minute:"2-digit",hour12:true});
}

// ─── Topic Popup Dialog ────────────────────────────────────────────────────────

const TOPIC_CONFIG = {
  screening: {
    icon: Search,
    color: "sky",
    label: "Screening Topic",
    plural: "Screening Topics",
    placeholder: "e.g. System Design, Leadership, Problem Solving…",
    description: "Topics guide AI interviewers to ask relevant screening questions.",
    badge: "bg-sky-50 dark:bg-sky-950/30 text-sky-700 dark:text-sky-300 border-sky-200 dark:border-sky-800",
    ring: "focus-visible:ring-sky-500/30",
    btn: "bg-sky-600 hover:bg-sky-700 text-white",
    chip: "bg-sky-50 dark:bg-sky-950/40 text-sky-700 dark:text-sky-300 border-sky-200/80 dark:border-sky-700/60",
    numberBg: "bg-sky-100 dark:bg-sky-900/60 text-sky-700 dark:text-sky-300",
    activeBorder: "border-sky-400 dark:border-sky-600",
    activeRow: "bg-sky-50/60 dark:bg-sky-950/20 border-sky-200 dark:border-sky-800",
  },
  coding: {
    icon: Code2,
    color: "violet",
    label: "Coding Question",
    plural: "Coding Questions",
    placeholder: "e.g. Implement binary search, Explain Big-O complexity…",
    description: "Specify exact coding questions or topic areas for challenges.",
    badge: "bg-violet-50 dark:bg-violet-950/30 text-violet-700 dark:text-violet-300 border-violet-200 dark:border-violet-800",
    ring: "focus-visible:ring-violet-500/30",
    btn: "bg-violet-600 hover:bg-violet-700 text-white",
    chip: "bg-violet-50 dark:bg-violet-950/40 text-violet-700 dark:text-violet-300 border-violet-200/80 dark:border-violet-700/60",
    numberBg: "bg-violet-100 dark:bg-violet-900/60 text-violet-700 dark:text-violet-300",
    activeBorder: "border-violet-400 dark:border-violet-600",
    activeRow: "bg-violet-50/60 dark:bg-violet-950/20 border-violet-200 dark:border-violet-800",
  },
};

function TopicPopupDialog({ open, type, cardName, existing = [], maxTopics = 4, onSave, onSkip, onCancel = null, totalSteps, currentStep }) {
  const [items, setItems]       = useState([...existing]);
  const [input, setInput]       = useState("");
  const [editingIdx, setEdit]   = useState(null);
  const [editVal, setEditVal]   = useState("");
  const [err, setErr]           = useState("");
  const inputRef                = useRef(null);
  const cfg                     = TOPIC_CONFIG[type] ?? TOPIC_CONFIG.screening;
  const Icon                    = cfg.icon;
  const atMax                   = items.length >= maxTopics;
  const canAdd                  = input.trim().length > 0 && !atMax;

  // Reset when dialog opens with new step
  useEffect(() => {
    if (open) {
      setItems([...existing]);
      setInput("");
      setEdit(null);
      setEditVal("");
      setErr("");
      setTimeout(() => inputRef.current?.focus(), 80);
    }
  }, [open, type]);

  const add = () => {
    const v = input.trim();
    if (!v) return;
    if (items.some(t => t.toLowerCase() === v.toLowerCase())) { setErr("Already added"); return; }
    setItems(p => [...p, v]);
    setInput("");
    setErr("");
    setTimeout(() => inputRef.current?.focus(), 20);
  };

  const remove = (i) => { setItems(p => p.filter((_, idx) => idx !== i)); if (editingIdx === i) setEdit(null); };

  const startEdit = (i) => { setEdit(i); setEditVal(items[i]); };
  const saveEdit  = () => {
    const v = editVal.trim();
    if (!v) return;
    setItems(p => p.map((t, i) => i === editingIdx ? v : t));
    setEdit(null); setEditVal("");
  };
  const cancelEdit = () => { setEdit(null); setEditVal(""); };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) (onCancel || onSkip)?.(); }}>
      <DialogContent
        className="max-w-[500px] p-0 gap-0 overflow-hidden border-border/60"
        onInteractOutside={(e) => e.preventDefault()}
      >
        {/* Progress bar */}
        {totalSteps > 1 && (
          <div className="h-0.5 bg-muted w-full">
            <div
              className="h-full transition-all duration-500 ease-out"
              style={{
                width: `${(currentStep / totalSteps) * 100}%`,
                background: type === "coding"
                  ? "linear-gradient(90deg,#7c3aed,#a855f7)"
                  : "linear-gradient(90deg,#0284c7,#38bdf8)",
              }}
            />
          </div>
        )}

        {/* Header */}
        <div className="px-6 pt-5 pb-4 border-b border-border/50">
          <div className="flex items-start gap-3">
            <div className={cn(
              "w-9 h-9 rounded-lg flex items-center justify-center shrink-0 mt-0.5",
              type === "coding"
                ? "bg-violet-100 dark:bg-violet-900/40"
                : "bg-sky-100 dark:bg-sky-900/40"
            )}>
              <Icon className={cn("w-4.5 h-4.5", type === "coding" ? "text-violet-600 dark:text-violet-400" : "text-sky-600 dark:text-sky-400")} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <DialogTitle className="text-sm font-semibold text-foreground leading-none">
                  Add {cfg.plural}
                </DialogTitle>
                <span className={cn("inline-flex items-center text-[10px] font-semibold px-1.5 py-0.5 rounded border", cfg.badge)}>
                  {cardName}
                </span>
                {totalSteps > 1 && (
                  <span className="text-[10px] text-muted-foreground ml-auto">
                    {currentStep} of {totalSteps}
                  </span>
                )}
              </div>
              <DialogDescription className="text-[12px] text-muted-foreground mt-1 leading-snug">
                {cfg.description}
              </DialogDescription>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="px-6 py-4 space-y-3 max-h-[340px] overflow-y-auto">
          {/* Input row */}
          <div className={cn("flex gap-2", atMax && "opacity-50 pointer-events-none")}>
            <div className="relative flex-1">
              <Input
                ref={inputRef}
                value={input}
                onChange={e => { setInput(e.target.value); setErr(""); }}
                onKeyDown={e => e.key === "Enter" && (e.preventDefault(), add())}
                placeholder={cfg.placeholder}
                maxLength={80}
                className={cn("h-9 text-sm pr-12", cfg.ring)}
                disabled={atMax}
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] text-muted-foreground tabular-nums">
                {items.length}/{maxTopics}
              </span>
            </div>
            <Button
              type="button"
              size="sm"
              onClick={add}
              disabled={!canAdd}
              className={cn("h-9 px-3 shrink-0 transition-all", cfg.btn, !canAdd && "opacity-40 cursor-not-allowed")}
            >
              <Plus className="w-3.5 h-3.5" />
            </Button>
          </div>

          {err && <p className="text-xs text-destructive -mt-1">{err}</p>}

          {/* Topics list */}
          {items.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 text-center rounded-xl border border-dashed border-border/60 bg-muted/20">
              <div className="w-10 h-10 rounded-full bg-muted/60 flex items-center justify-center mb-2">
                <Icon className="w-4.5 h-4.5 text-muted-foreground/60" />
              </div>
              <p className="text-xs font-medium text-muted-foreground">No {cfg.plural.toLowerCase()} yet</p>
              <p className="text-[11px] text-muted-foreground/70 mt-0.5">Add up to {maxTopics} above, or skip</p>
            </div>
          ) : (
            <div className="space-y-1.5">
              {items.map((item, i) => (
                <div
                  key={i}
                  className={cn(
                    "flex items-center gap-2.5 rounded-lg border px-3 py-2 transition-all duration-150",
                    editingIdx === i ? cfg.activeRow : "bg-muted/20 border-border/50 hover:border-border/80"
                  )}
                >
                  {/* Number */}
                  <span className={cn(
                    "w-5 h-5 rounded-md flex items-center justify-center text-[10px] font-bold shrink-0 transition-colors",
                    editingIdx === i ? cfg.numberBg : "bg-muted text-muted-foreground"
                  )}>
                    {i + 1}
                  </span>

                  {editingIdx === i ? (
                    /* Edit mode */
                    <>
                      <Input
                        value={editVal}
                        onChange={e => setEditVal(e.target.value)}
                        onKeyDown={e => {
                          if (e.key === "Enter") saveEdit();
                          if (e.key === "Escape") cancelEdit();
                        }}
                        autoFocus
                        className={cn("h-7 text-xs flex-1 border-0 bg-transparent p-0 shadow-none", cfg.ring)}
                      />
                      <div className="flex items-center gap-1 shrink-0">
                        <Button type="button" size="sm" onClick={saveEdit}
                          className={cn("h-6 px-2 text-[11px] font-semibold", cfg.btn)}>
                          Save
                        </Button>
                        <Button type="button" size="sm" variant="ghost" onClick={cancelEdit}
                          className="h-6 px-2 text-[11px] text-muted-foreground hover:text-foreground">
                          Cancel
                        </Button>
                      </div>
                    </>
                  ) : (
                    /* View mode */
                    <>
                      <span className="flex-1 text-xs font-medium text-foreground leading-snug line-clamp-2">
                        {item}
                      </span>
                      <div className="flex items-center gap-1 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                          type="button"
                          onClick={() => startEdit(i)}
                          className="w-6 h-6 rounded-md flex items-center justify-center hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
                          title="Edit"
                        >
                          <Pencil className="w-3 h-3" />
                        </button>
                        <button
                          type="button"
                          onClick={() => remove(i)}
                          className="w-6 h-6 rounded-md flex items-center justify-center hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition-colors"
                          title="Remove"
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                      {/* Always visible actions (better UX) */}
                      <div className="flex items-center gap-1 shrink-0">
                        <button
                          type="button"
                          onClick={() => startEdit(i)}
                          className="w-6 h-6 rounded-md flex items-center justify-center hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
                          title="Edit"
                        >
                          <Pencil className="w-3 h-3" />
                        </button>
                        <button
                          type="button"
                          onClick={() => remove(i)}
                          className="w-6 h-6 rounded-md flex items-center justify-center hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition-colors"
                          title="Remove"
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
          )}

          {atMax && (
            <p className="text-[11px] text-amber-600 dark:text-amber-400 flex items-center gap-1.5">
              <AlertTriangle className="w-3 h-3 shrink-0" />
              Max {maxTopics} reached — remove one to add another
            </p>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-3.5 border-t border-border/50 bg-muted/20 flex items-center justify-between gap-3">
          <Button type="button" variant="ghost" size="sm" onClick={() => onSkip()}
            className="text-muted-foreground hover:text-foreground h-8 text-xs">
            Skip for now
          </Button>
          <Button
            type="button"
            size="sm"
            onClick={() => onSave(items)}
            className={cn("h-8 gap-1.5 text-xs font-semibold", cfg.btn)}
          >
            {items.length > 0
              ? `Save ${items.length} ${items.length === 1 ? cfg.label : cfg.plural}`
              : "Continue"
            }
            <ArrowRight className="w-3.5 h-3.5" />
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─── Coding Question Dialog ────────────────────────────────────────────────────

function CodingQuestionDialog({ open, cardName, existing = [], onSave, onSkip, onCancel = null, totalSteps, currentStep, jobId }) {
  const MAX = 4;
  const [questions, setQuestions]     = useState([]);
  const [editing, setEditing]         = useState(null); // null | "new" | number
  const [form, setForm]               = useState({ title: "", description: "", sampleIO: [] });
  const [formErr, setFormErr]         = useState("");
  const [recommendations, setRecs]    = useState([]);
  const [recsLoading, setRecsLoading] = useState(false);
  const [recsOpen, setRecsOpen]       = useState(false);
  const descRef                       = useRef(null);

  useEffect(() => {
    if (open) {
      setQuestions(existing.length > 0 ? [...existing] : []);
      setEditing(null);
      setForm({ title: "", description: "", sampleIO: [] });
      setFormErr("");
      setRecsOpen(false);
    }
  }, [open]);

  // Fetch JD question library when dialog opens
  useEffect(() => {
    if (!open || !jobId) return;
    setRecsLoading(true);
    fetch(`${API_BASE_URL}/api/jd_coding_questions?job_id=${encodeURIComponent(jobId)}`, { credentials: "include" })
      .then(r => r.json())
      .then(d => setRecs(Array.isArray(d.questions) ? d.questions : []))
      .catch(() => setRecs([]))
      .finally(() => setRecsLoading(false));
  }, [open, jobId]);

  const applyRecommendation = (rec) => {
    // Avoid duplicates by description
    const descLower = (rec.description || "").trim().toLowerCase();
    const alreadyAdded = questions.some(q => (q.description || "").trim().toLowerCase() === descLower);
    if (alreadyAdded) { toast.info("This question is already in your list"); return; }
    if (questions.length >= MAX) { toast.warning(`Maximum ${MAX} questions reached`); return; }
    setQuestions(prev => [...prev, {
      title:       rec.title || "",
      description: rec.description || "",
      sampleIO:    Array.isArray(rec.sampleIO) ? rec.sampleIO : [],
    }]);
    toast.success("Question added from library");
  };

  const openAdd = () => {
    setForm({ title: "", description: "", sampleIO: [] });
    setEditing("new");
    setFormErr("");
    setTimeout(() => descRef.current?.focus(), 80);
  };
  const openEdit  = (i) => { setForm({ ...questions[i] }); setEditing(i); setFormErr(""); };
  const cancelEdit = () => { setEditing(null); setForm({ title: "", description: "", sampleIO: [] }); setFormErr(""); };

  const saveEdit = () => {
    if (!form.description.trim()) { setFormErr("Problem description is required"); return; }
    const q = {
      title: form.title.trim(),
      description: form.description.trim(),
      sampleIO: form.sampleIO.filter(io => io.input.trim() || io.expected.trim()),
    };
    if (editing === "new") {
      setQuestions(prev => [...prev, q]);
    } else {
      setQuestions(prev => prev.map((item, i) => i === editing ? q : item));
    }
    cancelEdit();
  };

  const removeQuestion = (i) => { setQuestions(prev => prev.filter((_, idx) => idx !== i)); if (editing === i) cancelEdit(); };
  const addIO    = () => setForm(f => ({ ...f, sampleIO: [...f.sampleIO, { input: "", expected: "" }] }));
  const removeIO = (i) => setForm(f => ({ ...f, sampleIO: f.sampleIO.filter((_, idx) => idx !== i) }));
  const updateIO = (i, field, val) => setForm(f => ({
    ...f,
    sampleIO: f.sampleIO.map((io, idx) => idx === i ? { ...io, [field]: val } : io),
  }));

  const atMax = questions.length >= MAX;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) (onCancel || onSkip)?.(); }}>
      <DialogContent
        className="max-w-[560px] p-0 gap-0 overflow-hidden border-border/60"
        onInteractOutside={(e) => e.preventDefault()}
      >
        {totalSteps > 1 && (
          <div className="h-0.5 bg-muted w-full">
            <div className="h-full bg-violet-600 transition-all duration-500 ease-out"
              style={{ width: `${(currentStep / totalSteps) * 100}%` }} />
          </div>
        )}

        <div className="px-6 pt-5 pb-4 border-b border-border/50">
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0 mt-0.5 bg-violet-100 dark:bg-violet-900/40">
              <Code2 className="w-4 h-4 text-violet-600 dark:text-violet-400" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <DialogTitle className="text-sm font-semibold text-foreground leading-none">
                  Custom Coding Questions
                </DialogTitle>
                <span className="inline-flex items-center text-[10px] font-semibold px-1.5 py-0.5 rounded border bg-violet-50 dark:bg-violet-950/30 text-violet-700 dark:text-violet-300 border-violet-200 dark:border-violet-800">
                  {cardName}
                </span>
                {totalSteps > 1 && (
                  <span className="text-[10px] text-muted-foreground ml-auto">{currentStep} of {totalSteps}</span>
                )}
              </div>
              <DialogDescription className="text-[12px] text-muted-foreground mt-1 leading-snug">
                Add your own problems with optional I/O examples. AI auto-generates boilerplate &amp; test cases for any missing fields.
              </DialogDescription>
            </div>
          </div>
        </div>

        <div className="px-6 py-4 space-y-3 max-h-[460px] overflow-y-auto">

          {/* ── JD Recommendations ── */}
          {(recsLoading || recommendations.length > 0) && (
            <div className="rounded-xl border border-amber-200 dark:border-amber-800 bg-amber-50/40 dark:bg-amber-950/10 overflow-hidden">
              <button
                type="button"
                onClick={() => setRecsOpen(v => !v)}
                className="w-full flex items-center gap-2 px-3 py-2 hover:bg-amber-50 dark:hover:bg-amber-950/20 transition-colors text-left"
              >
                <Sparkles className="w-3.5 h-3.5 text-amber-500 shrink-0" />
                <span className="text-[11px] font-semibold text-amber-700 dark:text-amber-300 flex-1">
                  {recsLoading ? "Loading saved questions…" : `${recommendations.length} question${recommendations.length !== 1 ? "s" : ""} saved for this job`}
                </span>
                <ChevronRight className={cn("w-3.5 h-3.5 text-amber-500 transition-transform", recsOpen && "rotate-90")} />
              </button>
              {recsOpen && !recsLoading && (
                <div className="px-3 pb-3 space-y-2 border-t border-amber-200/60 dark:border-amber-800/40 pt-2">
                  {recommendations.map((rec, ri) => {
                    const descLower = (rec.description || "").trim().toLowerCase();
                    const alreadyAdded = questions.some(q => (q.description || "").trim().toLowerCase() === descLower);
                    return (
                      <div key={ri} className="flex items-start gap-2 rounded-lg bg-white dark:bg-background/60 border border-amber-100 dark:border-amber-900/40 px-2.5 py-2">
                        <div className="flex-1 min-w-0">
                          {rec.title && <p className="text-[11px] font-semibold text-foreground leading-tight">{rec.title}</p>}
                          <p className="text-[11px] text-muted-foreground mt-0.5 line-clamp-2 leading-snug">{rec.description}</p>
                          {rec.sampleIO?.length > 0 && (
                            <p className="text-[10px] text-amber-600 dark:text-amber-400 mt-0.5 flex items-center gap-1">
                              <CheckCircle2 className="w-2.5 h-2.5" />{rec.sampleIO.length} I/O example{rec.sampleIO.length !== 1 ? "s" : ""}
                            </p>
                          )}
                        </div>
                        <button
                          type="button"
                          disabled={alreadyAdded || questions.length >= MAX}
                          onClick={() => applyRecommendation(rec)}
                          className={cn(
                            "shrink-0 text-[10px] font-semibold px-2 py-1 rounded-md border transition-colors",
                            alreadyAdded
                              ? "border-muted bg-muted text-muted-foreground cursor-default"
                              : questions.length >= MAX
                              ? "border-muted bg-muted text-muted-foreground cursor-not-allowed"
                              : "border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-950/30 text-amber-700 dark:text-amber-300 hover:bg-amber-100 dark:hover:bg-amber-900/30"
                          )}
                        >
                          {alreadyAdded ? "Added" : "Use"}
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Question list */}
          {questions.length === 0 && editing === null ? (
            <div className="flex flex-col items-center justify-center py-8 text-center rounded-xl border border-dashed border-border/60 bg-muted/20">
              <div className="w-10 h-10 rounded-full bg-muted/60 flex items-center justify-center mb-2">
                <Code2 className="w-4.5 h-4.5 text-muted-foreground/60" />
              </div>
              <p className="text-xs font-medium text-muted-foreground">No custom questions yet</p>
              <p className="text-[11px] text-muted-foreground/70 mt-0.5">Add up to {MAX} — AI generates the rest</p>
            </div>
          ) : (
            <div className="space-y-1.5">
              {questions.map((q, i) =>
                editing === i ? null : (
                  <div key={i} className="rounded-lg border bg-muted/20 border-border/50 px-3 py-2.5">
                    <div className="flex items-start gap-2.5">
                      <span className="w-5 h-5 rounded-md flex items-center justify-center text-[10px] font-bold shrink-0 bg-muted text-muted-foreground mt-0.5">
                        {i + 1}
                      </span>
                      <div className="flex-1 min-w-0">
                        {q.title && <p className="text-xs font-semibold text-foreground leading-tight">{q.title}</p>}
                        <p className="text-[11px] text-muted-foreground mt-0.5 line-clamp-2 leading-snug">{q.description}</p>
                        {q.sampleIO?.length > 0 ? (
                          <p className="text-[10px] text-violet-600 dark:text-violet-400 mt-1 flex items-center gap-1">
                            <CheckCircle2 className="w-2.5 h-2.5" />{q.sampleIO.length} I/O example{q.sampleIO.length !== 1 ? "s" : ""} provided
                          </p>
                        ) : (
                          <p className="text-[10px] text-amber-600 dark:text-amber-400 mt-1 flex items-center gap-1">
                            <Sparkles className="w-2.5 h-2.5" />AI will generate I/O &amp; test cases
                          </p>
                        )}
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <button type="button" onClick={() => openEdit(i)}
                          className="w-6 h-6 rounded-md flex items-center justify-center hover:bg-muted text-muted-foreground hover:text-foreground transition-colors">
                          <Pencil className="w-3 h-3" />
                        </button>
                        <button type="button" onClick={() => removeQuestion(i)}
                          className="w-6 h-6 rounded-md flex items-center justify-center hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition-colors">
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                  </div>
                )
              )}
            </div>
          )}

          {/* Inline add/edit form */}
          {editing !== null && (
            <div className="rounded-xl border border-violet-200 dark:border-violet-800 bg-violet-50/40 dark:bg-violet-950/10 p-4 space-y-3">
              <p className="text-xs font-semibold text-violet-700 dark:text-violet-300">
                {editing === "new" ? `New Question (${questions.length + 1}/${MAX})` : `Edit Question ${editing + 1}`}
              </p>

              <div className="space-y-1">
                <Label className="text-[11px] text-muted-foreground">Short Title (optional)</Label>
                <Input
                  value={form.title}
                  onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                  placeholder="e.g. Two Sum, Binary Search, Fibonacci…"
                  maxLength={80}
                  className="h-8 text-sm focus-visible:ring-violet-500/30"
                />
              </div>

              <div className="space-y-1">
                <Label className="text-[11px] text-muted-foreground">Problem Description <span className="text-destructive">*</span></Label>
                <Textarea
                  ref={descRef}
                  value={form.description}
                  onChange={e => { setForm(f => ({ ...f, description: e.target.value })); setFormErr(""); }}
                  placeholder={"Describe the full problem. e.g. Given an array of integers nums and an integer target, return indices of the two numbers such that they add up to target…"}
                  maxLength={2000}
                  rows={4}
                  className="text-sm resize-none focus-visible:ring-violet-500/30"
                />
                <div className="flex justify-between">
                  {formErr ? <p className="text-xs text-destructive">{formErr}</p> : <span />}
                  <span className="text-[10px] text-muted-foreground tabular-nums">{form.description.length}/2000</span>
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-[11px] text-muted-foreground">Sample I/O (optional)</Label>
                  {form.sampleIO.length < 5 && (
                    <button type="button" onClick={addIO}
                      className="text-[11px] text-violet-600 dark:text-violet-400 hover:underline flex items-center gap-1 font-medium">
                      <Plus className="w-3 h-3" />Add example
                    </button>
                  )}
                </div>
                {form.sampleIO.length === 0 ? (
                  <p className="text-[11px] text-muted-foreground/70 rounded-lg border border-dashed border-border/60 px-3 py-2 flex items-center gap-1.5">
                    <Sparkles className="w-3 h-3 text-amber-500 shrink-0" />
                    No I/O provided — AI will auto-generate all test cases
                  </p>
                ) : (
                  <div className="space-y-2">
                    {form.sampleIO.map((io, i) => (
                      <div key={i} className="flex gap-2 items-end">
                        <span className="text-[10px] font-bold text-muted-foreground pb-1.5">{i + 1}.</span>
                        <div className="flex-1 grid grid-cols-2 gap-2">
                          <div className="space-y-0.5">
                            <Label className="text-[10px] text-muted-foreground">Input</Label>
                            <Input value={io.input} onChange={e => updateIO(i, "input", e.target.value)}
                              placeholder="e.g. [2,7,11,15], 9" className="h-7 text-xs" />
                          </div>
                          <div className="space-y-0.5">
                            <Label className="text-[10px] text-muted-foreground">Expected Output</Label>
                            <Input value={io.expected} onChange={e => updateIO(i, "expected", e.target.value)}
                              placeholder="e.g. [0,1]" className="h-7 text-xs" />
                          </div>
                        </div>
                        <button type="button" onClick={() => removeIO(i)}
                          className="w-6 h-6 rounded flex items-center justify-center hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition-colors shrink-0 mb-0.5">
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex items-center gap-2 pt-1">
                <Button type="button" size="sm" onClick={saveEdit}
                  className="h-7 px-3 text-xs bg-violet-600 hover:bg-violet-700 text-white gap-1">
                  <CheckCircle2 className="w-3 h-3" />
                  {editing === "new" ? "Add Question" : "Save Changes"}
                </Button>
                <Button type="button" size="sm" variant="ghost" onClick={cancelEdit}
                  className="h-7 px-3 text-xs text-muted-foreground">
                  Cancel
                </Button>
              </div>
            </div>
          )}

          {editing === null && !atMax && (
            <button type="button" onClick={openAdd}
              className="w-full flex items-center justify-center gap-1.5 rounded-lg border border-dashed border-violet-300 dark:border-violet-700 py-2 text-[11px] font-medium text-violet-600 dark:text-violet-400 hover:bg-violet-50 dark:hover:bg-violet-950/20 transition-colors">
              <Plus className="w-3.5 h-3.5" />Add coding question ({questions.length}/{MAX})
            </button>
          )}

          {atMax && editing === null && (
            <p className="text-[11px] text-amber-600 dark:text-amber-400 flex items-center gap-1.5">
              <AlertTriangle className="w-3 h-3 shrink-0" />Max {MAX} questions reached
            </p>
          )}
        </div>

        <div className="px-6 py-3.5 border-t border-border/50 bg-muted/20 flex items-center justify-between gap-3">
          <Button type="button" variant="ghost" size="sm" onClick={() => onSkip()}
            className="text-muted-foreground hover:text-foreground h-8 text-xs">
            Skip for now
          </Button>
          <Button type="button" size="sm" onClick={() => onSave(questions)}
            className="h-8 gap-1.5 text-xs font-semibold bg-violet-600 hover:bg-violet-700 text-white">
            {questions.length > 0
              ? `Save ${questions.length} question${questions.length !== 1 ? "s" : ""}`
              : "Continue (AI generates all)"
            }
            <ArrowRight className="w-3.5 h-3.5" />
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─── Topic chip badges (shown on cards) ────────────────────────────────────────

function TopicChips({ screeningTopics = [], codingTopics = [], onEditScreening, onEditCoding }) {
  const hasAny = screeningTopics.length > 0 || codingTopics.length > 0;
  if (!hasAny) return null;
  return (
    <div className="flex flex-wrap gap-1 mt-2 pt-2 border-t border-border/30">
      {screeningTopics.map((t, i) => (
        <span key={`s${i}`} className="inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded-md border bg-sky-50 dark:bg-sky-950/30 text-sky-700 dark:text-sky-300 border-sky-200 dark:border-sky-700">
          <Search className="w-2.5 h-2.5 shrink-0" />{t}
        </span>
      ))}
      {codingTopics.map((t, i) => (
        <span key={`c${i}`} className="inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded-md border bg-violet-50 dark:bg-violet-950/30 text-violet-700 dark:text-violet-300 border-violet-200 dark:border-violet-700">
          <Code2 className="w-2.5 h-2.5 shrink-0" />{t}
        </span>
      ))}
      {(onEditScreening || onEditCoding) && (
        <button
          type="button"
          onClick={onEditScreening || onEditCoding}
          className="inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded-md border border-dashed border-border/60 text-muted-foreground hover:text-foreground hover:border-border transition-colors"
        >
          <Pencil className="w-2.5 h-2.5" />Edit topics
        </button>
      )}
    </div>
  );
}

// ─── Inline topic editor (for quick in-card access) ────────────────────────────

function QuickTopicEditor({ cardIdx, testType, topicsPerCard, codingQuestionsPerCard, onOpenPopup }) {
  const saved        = topicsPerCard[cardIdx] || {};
  const savedCoding  = codingQuestionsPerCard[cardIdx] ?? [];
  const hasScreening = containsScreening(testType);
  const hasCoding    = containsCoding(testType);
  if (!hasScreening && !hasCoding) return null;

  return (
    <div className="flex items-center gap-1.5 mt-2 pt-2 border-t border-border/30">
      <Sparkles className="w-3 h-3 text-muted-foreground/60 shrink-0" />
      <span className="text-[11px] text-muted-foreground">Customize:</span>
      {hasScreening && (
        <button
          type="button"
          onClick={() => onOpenPopup(cardIdx, "screening")}
          className={cn(
            "inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-md border transition-colors",
            (saved.screening?.length > 0)
              ? "bg-sky-50 dark:bg-sky-950/30 text-sky-700 dark:text-sky-300 border-sky-200 dark:border-sky-700"
              : "border-dashed border-border/60 text-muted-foreground hover:border-sky-400 hover:text-sky-600 dark:hover:text-sky-400"
          )}
        >
          <Search className="w-2.5 h-2.5" />
          {saved.screening?.length > 0 ? `${saved.screening.length} screening topic${saved.screening.length !== 1 ? "s" : ""}` : "Add screening topics"}
        </button>
      )}
      {hasCoding && (
        <button
          type="button"
          onClick={() => onOpenPopup(cardIdx, "coding")}
          className={cn(
            "inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-md border transition-colors",
            savedCoding.length > 0
              ? "bg-violet-50 dark:bg-violet-950/30 text-violet-700 dark:text-violet-300 border-violet-200 dark:border-violet-700"
              : "border-dashed border-border/60 text-muted-foreground hover:border-violet-400 hover:text-violet-600 dark:hover:text-violet-400"
          )}
        >
          <Code2 className="w-2.5 h-2.5" />
          {savedCoding.length > 0
            ? `${savedCoding.length} custom question${savedCoding.length !== 1 ? "s" : ""}`
            : "Add coding questions"}
        </button>
      )}
    </div>
  );
}

// ─── Credit Widget ─────────────────────────────────────────────────────────────

function CreditWidget({ balance, testCosts, sortedTests, totalCr = 0, loading, onRefresh }) {
  const numBalance = parseFloat(balance ?? "0");
  const remaining  = numBalance - totalCr;
  const canAfford  = remaining >= 0;
  const rateScreen = fmtCr(testCosts?.SCREENING_TEST ?? CANON_TEST_COSTS.SCREENING_TEST);
  const rateRT     = fmtCr(testCosts?.REALTIME_INTERVIEW ?? CANON_TEST_COSTS.REALTIME_INTERVIEW);
  const rateCoding = fmtCr(testCosts?.CODING_TEST ?? CANON_TEST_COSTS.CODING_TEST);

  return (
    <div className={cn(
      "rounded-xl border p-4 space-y-3 transition-colors",
      loading && "opacity-60",
      !canAfford && sortedTests.length > 0
        ? "border-destructive/40 bg-destructive/3"
        : "border-border/60 bg-muted/20"
    )}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className={cn("w-7 h-7 rounded-lg flex items-center justify-center",
            canAfford || !sortedTests.length ? "bg-primary/10" : "bg-destructive/10")}>
            <Wallet className={cn("w-3.5 h-3.5", canAfford || !sortedTests.length ? "text-primary" : "text-destructive")} />
          </div>
          <span className="text-xs font-semibold text-foreground tracking-wide uppercase">Credits</span>
        </div>
        <button type="button" onClick={onRefresh}
          className="text-muted-foreground hover:text-foreground transition-colors p-1 rounded-md hover:bg-muted">
          <RefreshCw className={cn("w-3.5 h-3.5", loading && "animate-spin")} />
        </button>
      </div>

      <div className="flex items-end justify-between gap-3">
        <div>
          <p className="text-[10px] text-muted-foreground uppercase tracking-widest mb-0.5">Available</p>
          <p className={cn("text-2xl font-bold tabular-nums leading-none",
            canAfford || !sortedTests.length ? "text-foreground" : "text-destructive")}>
            {loading ? "—" : numBalance.toFixed(numBalance % 1 === 0 ? 0 : 1)}
          </p>
        </div>
        {sortedTests.length > 0 && (
          <>
            <TrendingDown className="w-3.5 h-3.5 text-muted-foreground/40 mb-1" />
            <div className="text-right">
              <p className="text-[10px] text-muted-foreground uppercase tracking-widest mb-0.5">This batch</p>
              <p className={cn("text-2xl font-bold tabular-nums leading-none",
                canAfford ? "text-foreground" : "text-destructive")}>{fmtCr(totalCr)}</p>
            </div>
            <div className="text-right">
              <p className="text-[10px] text-muted-foreground uppercase tracking-widest mb-0.5">After</p>
              <p className={cn("text-2xl font-bold tabular-nums leading-none",
                canAfford ? "text-emerald-600 dark:text-emerald-400" : "text-destructive")}>
                {canAfford ? fmtCr(remaining) : "–"+fmtCr(Math.abs(remaining))}
              </p>
            </div>
          </>
        )}
      </div>

      <div className="flex items-center gap-2 flex-wrap pt-0.5">
        <span className="text-[10px] text-muted-foreground uppercase tracking-widest">Rates</span>
        <div className="flex gap-1.5 flex-wrap">
          <span className="inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full bg-sky-50 dark:bg-sky-950/30 text-sky-700 dark:text-sky-300 border border-sky-200 dark:border-sky-800">
            <span className="opacity-60">Screening</span>{rateScreen} cr
          </span>
          <span className="inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full bg-indigo-50 dark:bg-indigo-950/30 text-indigo-700 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-800">
            <span className="opacity-60">Realtime</span>{rateRT} cr
          </span>
          <span className="inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full bg-violet-50 dark:bg-violet-950/30 text-violet-700 dark:text-violet-300 border border-violet-200 dark:border-violet-800">
            <span className="opacity-60">Coding</span>{rateCoding} cr
          </span>
        </div>
        <span className="text-[10px] text-muted-foreground/70 w-full">Flat credits per test, per candidate.</span>
      </div>

      {!canAfford && sortedTests.length > 0 && (
        <div className="flex items-center gap-2 rounded-lg bg-destructive/8 border border-destructive/20 px-2.5 py-2">
          <AlertTriangle className="w-3.5 h-3.5 text-destructive shrink-0" />
          <span className="text-xs text-destructive font-medium">
            {fmtCr(Math.abs(remaining))} more credits needed
          </span>
        </div>
      )}
    </div>
  );
}

// ─── Flow pills / duration badge ──────────────────────────────────────────────

function FlowPills({ testType, testCosts }) {
  const comps = getComponents(testType);
  if (comps.length <= 1) return null;
  const LABEL = { NORMAL_TEST: "Assess", CODING_TEST: "Coding", REALTIME_TEST: "Realtime" };
  const perTest = testCosts?.FLOW_TEST ?? CANON_TEST_COSTS.FLOW_TEST;  // 1 credit / component
  return (
    <div className="flex flex-wrap gap-1 mt-1">
      {comps.map(({ action, mins }, i) => (
        <span key={i} className={cn(
          "inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded border",
          action === "CODING_TEST"
            ? "bg-violet-50 dark:bg-violet-950/20 text-violet-700 dark:text-violet-300 border-violet-200 dark:border-violet-800"
            : "bg-sky-50 dark:bg-sky-950/20 text-sky-700 dark:text-sky-300 border-sky-200 dark:border-sky-800"
        )}>
          {LABEL[action]??action}·{mins}m·{fmtCr(perTest)}cr
        </span>
      ))}
    </div>
  );
}

function DurationCreditBadge({ testType, testCosts, cr = null }) {
  const totalMins = getTotalMinutes(testType);
  const totalCr   = cr != null ? cr : testFixedCr(testType, testCosts);
  return (
    <div className="flex items-center gap-1 shrink-0">
      <Badge variant="secondary" className="text-[10px] gap-1 font-medium px-2 py-0.5">
        <Clock className="w-2.5 h-2.5" />
        {totalMins>=60 ? `${Math.floor(totalMins/60)}h${totalMins%60?` ${totalMins%60}m`:""}` : `${totalMins}m`}
      </Badge>
      <Badge className="text-[10px] gap-1 font-semibold px-2 py-0.5 bg-amber-50 dark:bg-amber-950/20 text-amber-700 dark:text-amber-300 border border-amber-200 dark:border-amber-700">
        <Zap className="w-2.5 h-2.5" />{fmtCr(totalCr)} cr
      </Badge>
    </div>
  );
}

// ─── Step indicator ───────────────────────────────────────────────────────────

function Steps({ step }) {
  const steps = ["Select Tests", "Configure & Schedule"];
  return (
    <div className="flex items-center gap-0 text-xs font-medium">
      {steps.map((label, i) => (
        <React.Fragment key={i}>
          <div className={cn("flex items-center gap-1.5 px-2.5 py-1 rounded-md transition-colors",
            i+1===step ? "bg-primary text-primary-foreground" :
            i+1<step   ? "text-primary" : "text-muted-foreground")}>
            <span className={cn("w-4 h-4 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0",
              i+1===step ? "bg-primary-foreground/20 text-primary-foreground" :
              i+1<step   ? "bg-primary/15 text-primary" : "bg-muted text-muted-foreground")}>
              {i+1<step ? <CheckCircle2 className="w-3 h-3"/> : i+1}
            </span>
            {label}
          </div>
          {i<steps.length-1 && (
            <div className={cn("h-px w-6 mx-1 transition-colors", i+1<step ? "bg-primary/40" : "bg-border")} />
          )}
        </React.Fragment>
      ))}
    </div>
  );
}

// ─── Aptitude Config Dialog ────────────────────────────────────────────────────

// Must mirror Web-Interviewer/services/aptitude_questions.py APTITUDE_SECTIONS
const APTITUDE_SECTIONS = [
  { key: "quantitative",        label: "Quantitative Aptitude", voiceCapable: true,  defaultMcq: 7, defaultVoice: 1 },
  { key: "logical",             label: "Logical Reasoning",     voiceCapable: true,  defaultMcq: 7, defaultVoice: 1 },
  { key: "verbal",              label: "Verbal Ability",        voiceCapable: true,  defaultMcq: 5, defaultVoice: 1 },
  { key: "data_interpretation", label: "Data Interpretation",   voiceCapable: false, defaultMcq: 4, defaultVoice: 0 },
  { key: "abstract",            label: "Abstract Reasoning",    voiceCapable: false, defaultMcq: 4, defaultVoice: 0 },
];

const EMPTY_APT_QUESTION = {
  section: "quantitative", question_type: "mcq", question: "",
  options: ["", "", "", ""], correct_answer: "", explanation: "",
};

function AptitudeConfigDialog({ open, cardName, existing, jobId, onSave, onSkip, onCancel = null, totalSteps, currentStep }) {
  const [sourceMode, setSourceMode] = useState(existing?.config?.source_mode ?? "ai");
  const [templateId, setTemplateId] = useState(existing?.config?.template_id ?? null);
  const [templates, setTemplates]   = useState([]);
  const [negativeMarking, setNegativeMarking] = useState(existing?.config?.negative_marking ?? false);
  const [duration, setDuration]     = useState(existing?.config?.duration_minutes ?? 30);
  const [sections, setSections]     = useState(() =>
    APTITUDE_SECTIONS.map(s => {
      const saved = (existing?.config?.sections || []).find(x => x.key === s.key);
      return {
        key: s.key, label: s.label, voiceCapable: s.voiceCapable,
        enabled: existing?.config?.sections ? !!saved : true,
        mcq_count: saved?.mcq_count ?? s.defaultMcq,
        voice_count: saved?.voice_count ?? s.defaultVoice,
      };
    })
  );
  const [customQuestions, setCustomQuestions] = useState(existing?.customQuestions ?? []);
  const [libraryCount, setLibraryCount] = useState(0);
  const [editing, setEditing] = useState(null); // null | question draft object

  useEffect(() => {
    if (!open) return;
    // Company templates for the source-mode dropdown
    fetch(`${API_BASE_URL}/api/aptitude/templates`, { credentials: "include" })
      .then(r => (r.ok ? r.json() : { templates: [] }))
      .then(d => setTemplates(d.templates || []))
      .catch(() => setTemplates([]));
    // Existing per-JD library (informational)
    if (jobId) {
      fetch(`${API_BASE_URL}/api/jd_aptitude_questions?job_id=${encodeURIComponent(jobId)}`, { credentials: "include" })
        .then(r => (r.ok ? r.json() : { questions: [] }))
        .then(d => setLibraryCount((d.questions || []).length))
        .catch(() => {});
    }
  }, [open, jobId]);

  const updateSection = (key, patch) =>
    setSections(prev => prev.map(s => (s.key === key ? { ...s, ...patch } : s)));

  const totalQuestions = sections.filter(s => s.enabled)
    .reduce((sum, s) => sum + (Number(s.mcq_count) || 0) + (s.voiceCapable ? (Number(s.voice_count) || 0) : 0), 0);

  const draftValid = editing && editing.question.trim() && (
    editing.question_type !== "mcq"
      ? editing.correct_answer.trim()
      : editing.options.every(o => o.trim()) && editing.options.includes(editing.correct_answer)
  );

  const handleAddQuestion = () => {
    if (!draftValid) return;
    setCustomQuestions(prev => [...prev, editing]);
    setEditing(null);
  };

  const handleSave = () => {
    if (sourceMode !== "template" && totalQuestions === 0) {
      toast.error("Enable at least one section with questions.");
      return;
    }
    if ((sourceMode === "template" || sourceMode === "mixed") && !templateId) {
      toast.error("Pick a template, or switch the question source to AI-generated.");
      return;
    }
    const config = {
      source_mode: sourceMode,
      template_id: templateId ? Number(templateId) : null,
      negative_marking: negativeMarking,
      duration_minutes: Math.max(10, Math.min(Number(duration) || 30, 120)),
      sections: sections.filter(s => s.enabled).map(s => ({
        key: s.key,
        mcq_count: Number(s.mcq_count) || 0,
        voice_count: s.voiceCapable ? (Number(s.voice_count) || 0) : 0,
      })),
    };
    onSave({ config, customQuestions });
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) (onCancel || onSkip)?.(); }}>
      <DialogContent className="max-w-[760px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-primary" />
            Configure Aptitude Test{cardName ? ` — ${cardName}` : ""}
          </DialogTitle>
          <DialogDescription>
            Choose sections, question counts, and question source.
            {totalSteps > 1 ? ` (Step ${currentStep} of ${totalSteps})` : ""}
          </DialogDescription>
        </DialogHeader>

        {/* Source mode */}
        <div className="space-y-2">
          <Label className="text-sm font-semibold">Question source</Label>
          <div className="flex flex-wrap gap-2">
            {[
              { id: "ai",       label: "Automated" },
              { id: "template", label: "Company template" },
              { id: "mixed",    label: "Template + automated fill" },
            ].map(m => (
              <Button
                key={m.id}
                type="button"
                variant={sourceMode === m.id ? "default" : "outline"}
                size="sm"
                onClick={() => setSourceMode(m.id)}
              >
                {m.label}
              </Button>
            ))}
          </div>
          {(sourceMode === "template" || sourceMode === "mixed") && (
            <div className="pt-1">
              {templates.length === 0 ? (
                <p className="text-xs text-muted-foreground">
                  No aptitude templates yet — create one in the Aptitude Builder, or use automated questions.
                </p>
              ) : (
                <Select value={templateId ? String(templateId) : ""} onValueChange={v => setTemplateId(v)}>
                  <SelectTrigger className="w-[320px]">
                    <SelectValue placeholder="Select a company template" />
                  </SelectTrigger>
                  <SelectContent>
                    {templates.map(t => (
                      <SelectItem key={t.id} value={String(t.id)}>
                        {t.name} ({t.question_count} questions)
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          )}
        </div>

        {/* Sections */}
        {sourceMode !== "template" && (
          <div className="space-y-2">
            <Label className="text-sm font-semibold">Sections ({totalQuestions} questions total)</Label>
            <div className="space-y-1.5">
              {sections.map(s => (
                <div key={s.key} className={cn(
                  "flex items-center gap-3 p-2.5 rounded-md border",
                  s.enabled ? "border-primary/40 bg-primary/5" : "border-border opacity-60",
                )}>
                  <input
                    type="checkbox"
                    checked={s.enabled}
                    onChange={e => updateSection(s.key, { enabled: e.target.checked })}
                    className="w-4 h-4 accent-current cursor-pointer"
                  />
                  <span className="flex-1 text-sm font-medium">{s.label}</span>
                  <div className="flex items-center gap-1.5">
                    <Label className="text-xs text-muted-foreground">MCQ</Label>
                    <Input
                      type="number" min={0} max={25}
                      value={s.mcq_count}
                      disabled={!s.enabled}
                      onChange={e => updateSection(s.key, { mcq_count: e.target.value })}
                      className="w-16 h-8 text-sm"
                    />
                  </div>
                  {s.voiceCapable && (
                    <div className="flex items-center gap-1.5">
                      <Label className="text-xs text-muted-foreground">Verbal</Label>
                      <Input
                        type="number" min={0} max={3}
                        value={s.voice_count}
                        disabled={!s.enabled}
                        onChange={e => updateSection(s.key, { voice_count: e.target.value })}
                        className="w-14 h-8 text-sm"
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Options */}
        <div className="flex flex-wrap items-center gap-6">
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input
              type="checkbox"
              checked={negativeMarking}
              onChange={e => setNegativeMarking(e.target.checked)}
              className="w-4 h-4 cursor-pointer"
            />
            Negative marking (−0.25 per wrong MCQ)
          </label>
          <div className="flex items-center gap-2">
            <Label className="text-sm">Duration</Label>
            <Input
              type="number" min={10} max={120}
              value={duration}
              onChange={e => setDuration(e.target.value)}
              className="w-20 h-8 text-sm"
            />
            <span className="text-xs text-muted-foreground">minutes</span>
          </div>
        </div>

        {/* Custom questions */}
        <div className="space-y-2 border-t pt-3">
          <div className="flex items-center justify-between">
            <Label className="text-sm font-semibold">
              Your custom questions ({customQuestions.length})
              {libraryCount > 0 && (
                <span className="ml-2 text-xs font-normal text-muted-foreground">
                  + {libraryCount} already saved for this job (used automatically)
                </span>
              )}
            </Label>
            {!editing && (
              <Button variant="outline" size="sm" onClick={() => setEditing({ ...EMPTY_APT_QUESTION })}>
                <Plus className="w-3.5 h-3.5 mr-1" /> Add question
              </Button>
            )}
          </div>

          {customQuestions.map((q, i) => (
            <div key={i} className="flex items-start gap-2 p-2 rounded border border-border text-sm">
              <Badge variant="secondary" className="text-[10px] shrink-0 mt-0.5">
                {APTITUDE_SECTIONS.find(s => s.key === q.section)?.label ?? q.section} · {q.question_type.toUpperCase()}
              </Badge>
              <span className="flex-1 line-clamp-2">{q.question}</span>
              <Button
                variant="ghost" size="icon" className="h-6 w-6 shrink-0"
                onClick={() => setCustomQuestions(prev => prev.filter((_, j) => j !== i))}
              >
                <Trash2 className="w-3.5 h-3.5 text-destructive" />
              </Button>
            </div>
          ))}

          {editing && (
            <Card className="p-3 space-y-2.5">
              <div className="flex gap-2">
                <Select value={editing.section} onValueChange={v => setEditing(e => ({ ...e, section: v }))}>
                  <SelectTrigger className="w-[220px] h-8 text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {APTITUDE_SECTIONS.map(s => (
                      <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select
                  value={editing.question_type}
                  onValueChange={v => setEditing(e => ({
                    ...e,
                    question_type: v,
                    correct_answer: (v !== "mcq" && e.question_type !== "mcq") ? e.correct_answer : "",
                  }))}
                >
                  <SelectTrigger className="w-[150px] h-8 text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="mcq">MCQ</SelectItem>
                    <SelectItem value="voice">Verbal answer</SelectItem>
                    <SelectItem value="written">Written answer</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Textarea
                placeholder="Question text…"
                value={editing.question}
                onChange={e => setEditing(d => ({ ...d, question: e.target.value }))}
                className="min-h-[70px] text-sm"
              />
              {editing.question_type === "mcq" ? (
                <div className="space-y-1.5">
                  {editing.options.map((opt, oi) => (
                    <div key={oi} className="flex items-center gap-2">
                      <input
                        type="radio"
                        name="apt-correct"
                        checked={editing.correct_answer === opt && opt !== ""}
                        onChange={() => setEditing(d => ({ ...d, correct_answer: opt }))}
                        className="cursor-pointer"
                        title="Mark as correct answer"
                      />
                      <Input
                        placeholder={`Option ${oi + 1}`}
                        value={opt}
                        onChange={e => setEditing(d => {
                          const options = [...d.options];
                          const wasCorrect = d.correct_answer === options[oi] && options[oi] !== "";
                          options[oi] = e.target.value;
                          return { ...d, options, correct_answer: wasCorrect ? e.target.value : d.correct_answer };
                        })}
                        className="h-8 text-sm"
                      />
                    </div>
                  ))}
                  <p className="text-xs text-muted-foreground">Select the radio next to the correct option.</p>
                </div>
              ) : (
                <Textarea
                  placeholder="Expected answer / grading notes (what a full-credit spoken answer contains)…"
                  value={editing.correct_answer}
                  onChange={e => setEditing(d => ({ ...d, correct_answer: e.target.value }))}
                  className="min-h-[60px] text-sm"
                />
              )}
              <div className="flex justify-end gap-2">
                <Button variant="ghost" size="sm" onClick={() => setEditing(null)}>Cancel</Button>
                <Button size="sm" disabled={!draftValid} onClick={handleAddQuestion}>Add</Button>
              </div>
            </Card>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onSkip()}>Use defaults</Button>
          <Button onClick={handleSave}>
            <CheckCircle2 className="w-4 h-4 mr-1.5" /> Save configuration
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function InterviewSchedulePopup({ candidate, onClose, jobDetails }) {
  const [step, setStep]           = useState(1);
  const [selectedCards, setCards] = useState([]);
  const [difficulty, setDiff]     = useState("medium");
  const [testTimes, setTimes]     = useState({});
  const [languages, setLangs]     = useState({});
  const [platforms, setPlatforms] = useState({});
  const [isScheduling, setSched]  = useState(false);
  const [showSuccess, setSuccess] = useState(false);
  const [progress, setProgress]   = useState({ current: null, completed: [] });
  const [created, setCreated]     = useState([]);

  // Credits + plan entitlements (one call: /api/entitlements/me)
  const [ent, setEnt]                 = useState(null);   // {enforced, features, test_costs, plan_name}
  const [balance, setBalance]         = useState(null);
  const [creditLoading, setCreditLoad] = useState(false);
  const testCosts = ent?.test_costs || CANON_TEST_COSTS;

  // ── Topics state ────────────────────────────────────────────────────────────
  // topicsPerCard: { [cardIdx]: { screening: string[] } }
  const [topicsPerCard, setTopicsPerCard] = useState({});
  // codingQuestionsPerCard: { [cardIdx]: Array<{title,description,sampleIO}> }
  const [codingQuestionsPerCard, setCodingQuestionsPerCard] = useState({});
  // aptitudeConfigPerCard: { [cardIdx]: { config, customQuestions } }
  const [aptitudeConfigPerCard, setAptitudeConfigPerCard] = useState({});

  // Realtime P&B custom-question sets (Settings → P&B Builder). Per-card choice
  // keyed by card index; "" / undefined = let Sana generate questions from the JD.
  const [pnbTemplates, setPnbTemplates]             = useState([]);   // [{id,name,question_count}]
  const [pnbTemplatesLoaded, setPnbTemplatesLoaded] = useState(false);
  const [pnbTemplateByIdx, setPnbTemplateByIdx]     = useState({});   // { [cardIdx]: templateId }

  // Popup queue: [{ cardIdx, type, cardName }]
  const [topicQueue, setTopicQueue]       = useState([]);
  const [topicQueueIdx, setTopicQueueIdx] = useState(null);

  // Manual single-card popup (from "Add topics" button on card)
  const [manualPopup, setManualPopup] = useState(null); // { cardIdx, type }
  const [aptitudeInlineIdx, setAptitudeInlineIdx] = useState(null); // card idx for inline aptitude config

  const sortedTests = useMemo(() => sortTests(selectedCards), [selectedCards]);
  // Per-card credit cost — aptitude depends on the chosen source (template=1, AI=2).
  const cardCr = useCallback((tt, idx) => (
    tt === "/aptitude"
      ? aptitudeCr(testCosts, aptitudeConfigPerCard?.[idx]?.config?.source_mode)
      : testFixedCr(tt, testCosts)
  ), [testCosts, aptitudeConfigPerCard]);
  const totalCr = useMemo(
    () => sortedTests.reduce((s, tt, idx) => s + cardCr(tt, idx), 0),
    [sortedTests, cardCr],
  );
  const canAfford   = parseFloat(balance ?? "0") >= totalCr;

  // ── Plan feature locks (only bite when enforcement is ON) ─────────────────
  const isTestLocked = useCallback((tt) => {
    if (!ent?.enforced) return false;
    const feats = ent.features || {};
    return featuresForTest(tt).some((f) => feats[f] === false);
  }, [ent]);
  // Lock map for the selection grid: { [testId]: {locked, reason} }.
  const lockInfo = useMemo(() => {
    if (!ent?.enforced) return {};
    const feats = ent.features || {};
    const planLbl = ent.plan_name ? `${ent.plan_name} plan` : "your plan";
    const map = {};
    [...Object.keys(FEATURE_BY_TEST), ...Object.keys(FLOW_FEATURES)].forEach((tt) => {
      if (featuresForTest(tt).some((f) => feats[f] === false)) {
        map[tt] = { locked: true, reason: `Not included in ${planLbl}` };
      }
    });
    return map;
  }, [ent]);
  const lockedSelected = useMemo(() => sortedTests.filter(isTestLocked), [sortedTests, isTestLocked]);
  const hasLocked      = lockedSelected.length > 0;

  // ── Load the company's saved P&B question sets — only when a Realtime P&B
  //    card is actually being scheduled, so we never hit the endpoint needlessly.
  const hasRealtimePnb = useMemo(
    () => sortedTests.some((tt) => String(tt) === "/realtime-pnb"),
    [sortedTests],
  );
  useEffect(() => {
    if (!hasRealtimePnb || pnbTemplatesLoaded) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/pnb/templates`, { credentials: "include" });
        if (!res.ok) throw new Error(`Status ${res.status}`);
        const data = await res.json();
        if (!cancelled) setPnbTemplates(Array.isArray(data.templates) ? data.templates : []);
      } catch (e) {
        console.error("[InterviewSchedulePopup] load P&B templates failed", e);
        if (!cancelled) setPnbTemplates([]);
      } finally {
        if (!cancelled) setPnbTemplatesLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, [hasRealtimePnb, pnbTemplatesLoaded]);

  // ── Load credits + entitlements (single call) ─────────────────────────────
  const loadCredits = useCallback(async () => {
    setCreditLoad(true);
    try {
      const companyId = localStorage.getItem("companyAccountId");
      const jobId = candidate?.job_id;
      const qs = companyId
        ? `company_id=${encodeURIComponent(companyId)}`
        : (jobId ? `job_id=${encodeURIComponent(jobId)}` : "");
      const res  = await fetch(`${API_BASE_URL}/api/entitlements/me?${qs}`, { credentials: "include" });
      const data = await res.json();
      setEnt(data || null);
      setBalance(data?.balance != null ? String(data.balance) : "0");
    } catch (e) {
      console.error("[entitlements]", e);
      setEnt(null);
    } finally { setCreditLoad(false); }
  }, [candidate]);

  useEffect(() => { loadCredits(); }, [loadCredits]);

  // ── Platform defaults ───────────────────────────────────────────────────────
  useEffect(() => {
    setPlatforms(prev => {
      const next = { ...prev };
      sortedTests.forEach((_, i) => { if (!next[i]) next[i] = "web"; });
      Object.keys(next).forEach(k => { if (+k >= sortedTests.length) delete next[k]; });
      return next;
    });
  }, [sortedTests]);

  // ── Time cascade ────────────────────────────────────────────────────────────
  const handleTimeChange = useCallback((value, idx) => {
    setTimes(prev => {
      const next = { ...prev, [idx]: value };
      for (let i = idx+1; i < sortedTests.length; i++) {
        if (!next[i-1]) break;
        const dt = new Date(next[i-1]);
        dt.setMinutes(dt.getMinutes() + getTotalMinutes(sortedTests[i-1]));
        next[i] = toInputValue(dt);
      }
      return next;
    });
  }, [sortedTests]);

  // ── Validation ──────────────────────────────────────────────────────────────
  const allTimesSet = sortedTests.every((_, i) => !!testTimes[i]);
  const allLangsSet = sortedTests.every((tt, i) => containsCoding(tt) ? !!languages[i] : true);
  const onlyPB      = sortedTests.length > 0 && sortedTests.every(tt =>
    getComponents(tt).every(c => c.action === "NORMAL_TEST") &&
    (tt.includes("personality")||tt.includes("behavioral")||tt.includes("all-test")||tt.includes("pnb"))
  );
  const diffOk             = onlyPB || !!difficulty;
  const readyToSchedule    = allTimesSet && diffOk && allLangsSet && canAfford && !hasLocked;

  // ── Topic queue helpers ─────────────────────────────────────────────────────

  // Only prompt for steps that haven't been configured yet via the manual popup
  const buildQueue = (tests) =>
    tests.flatMap((testType, cardIdx) => {
      const steps = [];
      const savedScreening = (topicsPerCard[cardIdx] || {}).screening;
      const savedCodingQs  = codingQuestionsPerCard[cardIdx];
      if (containsScreening(testType) && !(savedScreening?.length > 0))
        steps.push({ cardIdx, type: "screening", cardName: displayName(testType) });
      if (containsCoding(testType) && !(savedCodingQs?.length > 0))
        steps.push({ cardIdx, type: "coding", cardName: displayName(testType) });
      if (testType === "/aptitude" && !aptitudeConfigPerCard[cardIdx])
        steps.push({ cardIdx, type: "aptitude", cardName: displayName(testType) });
      return steps;
    });

  // Kick off queue before scheduling
  const startTopicQueue = () => {
    const queue = buildQueue(sortedTests);
    if (queue.length === 0) { runScheduling(topicsPerCard, codingQuestionsPerCard); return; }
    setTopicQueue(queue);
    setTopicQueueIdx(0);
  };

  const advanceQueue = (updatedTopics, updatedCodingQs, updatedAptitude = aptitudeConfigPerCard) => {
    const next = topicQueueIdx + 1;
    if (next < topicQueue.length) {
      setTopicsPerCard(updatedTopics);
      setCodingQuestionsPerCard(updatedCodingQs);
      setAptitudeConfigPerCard(updatedAptitude);
      setTopicQueueIdx(next);
    } else {
      setTopicQueueIdx(null);
      setTopicQueue([]);
      runScheduling(updatedTopics, updatedCodingQs, updatedAptitude);
    }
  };

  const handleQueueSave = (items) => {
    const step = topicQueue[topicQueueIdx];
    if (step.type === "coding") {
      advanceQueue(topicsPerCard, { ...codingQuestionsPerCard, [step.cardIdx]: items });
    } else if (step.type === "aptitude") {
      advanceQueue(topicsPerCard, codingQuestionsPerCard, { ...aptitudeConfigPerCard, [step.cardIdx]: items });
    } else {
      const updated = {
        ...topicsPerCard,
        [step.cardIdx]: { ...(topicsPerCard[step.cardIdx] || {}), [step.type]: items },
      };
      advanceQueue(updated, codingQuestionsPerCard);
    }
  };

  // X / Esc on any queue-step dialog aborts the WHOLE scheduling run —
  // nothing has been scheduled or charged yet (credits are only consumed
  // inside runScheduling), so cancelling here is always safe.
  const cancelQueue = () => {
    setTopicQueue([]);
    setTopicQueueIdx(null);
    toast.info("Scheduling cancelled — nothing was scheduled.");
  };

  const handleQueueSkip = () => {
    const step = topicQueue[topicQueueIdx];
    if (step.type === "coding") {
      advanceQueue(topicsPerCard, { ...codingQuestionsPerCard, [step.cardIdx]: [] });
    } else if (step.type === "aptitude") {
      // "Use defaults" — schedule with the backend's default section mix
      advanceQueue(topicsPerCard, codingQuestionsPerCard, {
        ...aptitudeConfigPerCard,
        [step.cardIdx]: { config: {}, customQuestions: [] },
      });
    } else {
      const updated = {
        ...topicsPerCard,
        [step.cardIdx]: { ...(topicsPerCard[step.cardIdx] || {}), [step.type]: [] },
      };
      advanceQueue(updated, codingQuestionsPerCard);
    }
  };

  // Manual single popup (from card button)
  const openManualPopup = (cardIdx, type) => setManualPopup({ cardIdx, type });
  const handleManualSave = (items) => {
    if (!manualPopup) return;
    if (manualPopup.type === "coding") {
      setCodingQuestionsPerCard(prev => ({ ...prev, [manualPopup.cardIdx]: items }));
    } else {
      setTopicsPerCard(prev => ({
        ...prev,
        [manualPopup.cardIdx]: { ...(prev[manualPopup.cardIdx] || {}), [manualPopup.type]: items },
      }));
    }
    setManualPopup(null);
  };

  // ── Schedule button click ───────────────────────────────────────────────────
  const handleScheduleClick = () => {
    if (hasLocked) {
      const names = lockedSelected.map(displayName).join(", ");
      toast.error(`${names} ${lockedSelected.length > 1 ? "aren't" : "isn't"} in your ${ent?.plan_name || "current"} plan. Remove ${lockedSelected.length > 1 ? "them" : "it"} or upgrade.`);
      return;
    }
    if (!allTimesSet)  { toast.error("Set a time for every test."); return; }
    if (!diffOk)       { toast.error("Select a difficulty level."); return; }
    if (!allLangsSet)  { toast.error("Select a language for coding tests."); return; }
    if (!canAfford)    { toast.error(`Insufficient credits. Need ${fmtCr(totalCr)}, available ${balance}.`); return; }
    startTopicQueue();
  };

  // ── Actual scheduling ───────────────────────────────────────────────────────
  const runScheduling = async (finalTopics, finalCodingQs = codingQuestionsPerCard, finalAptitude = aptitudeConfigPerCard) => {
    const employerEmail = sessionStorage.getItem("employer_email") ?? localStorage.getItem("employer_email");
    if (!employerEmail) {
      if (window.confirm("You need to log in as an employer. Go to login?"))
        window.open(`${API_BASE_URL}/login`, "_blank");
      return;
    }
    const companyId = localStorage.getItem("companyAccountId");
    if (!companyId) { toast.error("Company ID not found. Please log in again."); return; }

    try {
      setSched(true);
      setProgress({ current: null, completed: [] });
      const results = [];

      for (let idx = 0; idx < sortedTests.length; idx++) {
        const testType = sortedTests[idx];
        const isRT     = testType.startsWith("/realtime-");
        const needLang = containsCoding(testType);
        const cardTopics  = finalTopics[idx] || {};
        const cardCodingQs = (finalCodingQs[idx] ?? []);

        // Debug log removed — was leaking the personalized coding questions
        // (full problem text + expected solutions) to console.

        const payload = new URLSearchParams({
          jobId:           candidate.job_id ?? "",
          scheduledTime:   testTimes[idx],
          selected_users:  candidate.candidate_email,
          topic:           jobDetails?.job_title ?? "",
          companyId,
          test_type:       testType,
          employer_email:  employerEmail,
          test_platform:   platforms[idx] ?? "web",
          difficulty:      onlyPB ? "Intermediate" : difficulty,
          // ── topic arrays ──
          screening_topics:              JSON.stringify(cardTopics.screening ?? []),
          coding_topics:                 JSON.stringify([]),
          personalized_coding_questions: JSON.stringify(cardCodingQs),
        });
        if (needLang) payload.append("language", languages[idx] ?? "");
        if (isRT) { payload.append("mode","realtime"); payload.append("is_realtime","1"); }
        // Realtime P&B: attach the chosen custom question set (if any). Empty =
        // Sana generates questions from the JD, so we only send a real id.
        if (testType === "/realtime-pnb" && pnbTemplateByIdx[idx]) {
          payload.append("pnb_template_id", String(pnbTemplateByIdx[idx]));
        }
        if (testType === "/aptitude") {
          const aptCard = finalAptitude[idx] ?? { config: {}, customQuestions: [] };
          payload.append("aptitude_config", JSON.stringify(aptCard.config ?? {}));
          payload.append("custom_aptitude_questions", JSON.stringify(aptCard.customQuestions ?? []));
        }

        setProgress(p => ({ ...p, current: { id: testType } }));

        const res = await fetch(`${API_BASE_URL}/api/schedule_test_jobId`, {
          method:"POST", credentials:"include",
          headers:{"Content-Type":"application/x-www-form-urlencoded"},
          body: payload.toString(),
        });
        if (!res.ok) {
          // Parse a structured error so plan/credit blocks read cleanly.
          let info = null;
          try { info = await res.clone().json(); } catch { /* not JSON */ }
          if (res.status === 403 && info?.upgrade_required) {
            throw new Error(`${displayName(testType)} isn't included in your ${ent?.plan_name || "current"} plan. Upgrade to schedule it.`);
          }
          if (res.status === 402) {
            throw new Error(`Not enough credits for ${displayName(testType)}${info?.required ? ` (needs ${fmtCr(Number(info.required))} cr)` : ""}. Please top up.`);
          }
          if (info?.reason === "monthly_limit_reached") {
            // Tests go one at a time, so earlier ones may already be scheduled.
            const limitErr = new Error(info.error);
            limitErr.isMonthlyLimit = true;
            limitErr.scheduled = results.map(r => displayName(r.cardId));
            throw limitErr;
          }
          throw new Error(info?.error || (await res.text()) || `Failed: ${displayName(testType)}`);
        }

        const data = await res.json();
        results.push({
          testId: data.test_id ?? data.id ?? `t-${idx}`,
          cardId: testType,
          scheduledTime: testTimes[idx],
          screeningTopics: cardTopics.screening ?? [],
          codingTopics:    cardTopics.coding    ?? [],
        });
        setProgress(p => ({ ...p, completed: [...p.completed, testType], current: null }));
      }

      setCreated(results);
      setSuccess(true);
      setTimeout(() => { setSuccess(false); onClose(); window.location.reload(true); }, 4000);
    } catch (err) {
      if (err?.isMonthlyLimit) {
        const done = err.scheduled || [];
        toast.error(
          done.length
            ? `${done.join(", ")} ${done.length === 1 ? "was" : "were"} scheduled. The remaining tests were not: ${err.message}`
            : err.message,
          { duration: 12000 },
        );
      } else {
        toast.error("Scheduling failed: " + (err.message ?? err));
      }
    } finally {
      setSched(false);
    }
  };

  const difficulties    = ["easy", "medium", "hard"];
  // Languages this list MUST stay in sync with what the backend actually
  // handles (see Web-Interviewer/services/coding_questions.py
  // `_make_boilerplate` + utils/prompts.py boilerplate schema + the
  // candidate's iCode.jsx language picker). Canonical casing matters:
  // - "C++" not "cpp" — the boilerplate dict key is "C++"; sending "cpp"
  //   means the candidate's editor loads an empty starter file.
  // - "Csharp" was removed 2026-06-05 — the backend has no C# pipeline
  //   (no boilerplate, no tokenizer, no AI prompt mention) so picking it
  //   silently broke every C# test.
  // - "C" and "NodeJS" added so this scheduler matches the other 5
  //   schedulers (DScheduleTest, ScheduleTestModal, ScheduleTest,
  //   SearchJobAI/CompanyAdmin equivalents).
  const languageOptions = ["Python", "JavaScript", "NodeJS", "C++", "C", "Java"];

  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <>
      {/* ── Manual single-card popup ── */}
      {manualPopup && manualPopup.type === "coding" ? (
        <CodingQuestionDialog
          open={true}
          cardName={displayName(sortedTests[manualPopup.cardIdx])}
          existing={codingQuestionsPerCard[manualPopup.cardIdx] ?? []}
          onSave={handleManualSave}
          onSkip={() => setManualPopup(null)}
          onCancel={() => setManualPopup(null)}
          totalSteps={1}
          currentStep={1}
          jobId={candidate?.job_id}
        />
      ) : manualPopup ? (
        <TopicPopupDialog
          open={true}
          type={manualPopup.type}
          cardName={displayName(sortedTests[manualPopup.cardIdx])}
          existing={(topicsPerCard[manualPopup.cardIdx] || {})[manualPopup.type] || []}
          maxTopics={4}
          onSave={handleManualSave}
          onSkip={() => setManualPopup(null)}
          onCancel={() => setManualPopup(null)}
          totalSteps={1}
          currentStep={1}
        />
      ) : null}

      {/* ── Pre-schedule topic collection queue ── */}
      {topicQueueIdx !== null && topicQueueIdx < topicQueue.length && (
        topicQueue[topicQueueIdx].type === "coding" ? (
          <CodingQuestionDialog
            open={true}
            cardName={topicQueue[topicQueueIdx].cardName}
            existing={codingQuestionsPerCard[topicQueue[topicQueueIdx].cardIdx] ?? []}
            onSave={handleQueueSave}
            onSkip={handleQueueSkip}
            onCancel={cancelQueue}
            totalSteps={topicQueue.length}
            currentStep={topicQueueIdx + 1}
            jobId={candidate?.job_id}
          />
        ) : topicQueue[topicQueueIdx].type === "aptitude" ? (
          <AptitudeConfigDialog
            open={true}
            cardName={topicQueue[topicQueueIdx].cardName}
            existing={aptitudeConfigPerCard[topicQueue[topicQueueIdx].cardIdx] ?? null}
            jobId={candidate?.job_id}
            onSave={handleQueueSave}
            onSkip={handleQueueSkip}
            onCancel={cancelQueue}
            totalSteps={topicQueue.length}
            currentStep={topicQueueIdx + 1}
          />
        ) : (
          <TopicPopupDialog
            open={true}
            type={topicQueue[topicQueueIdx].type}
            cardName={topicQueue[topicQueueIdx].cardName}
            existing={(topicsPerCard[topicQueue[topicQueueIdx].cardIdx] || {})[topicQueue[topicQueueIdx].type] || []}
            maxTopics={4}
            onSave={handleQueueSave}
            onSkip={handleQueueSkip}
            onCancel={cancelQueue}
            totalSteps={topicQueue.length}
            currentStep={topicQueueIdx + 1}
          />
        )
      )}

      {/* ── Inline aptitude config (opened from a card at step 2) ── */}
      {aptitudeInlineIdx !== null && (
        <AptitudeConfigDialog
          open={true}
          cardName={displayName(sortedTests[aptitudeInlineIdx])}
          existing={aptitudeConfigPerCard[aptitudeInlineIdx] ?? null}
          jobId={candidate?.job_id}
          onSave={(items) => {
            setAptitudeConfigPerCard(prev => ({ ...prev, [aptitudeInlineIdx]: items }));
            setAptitudeInlineIdx(null);
          }}
          onSkip={() => setAptitudeInlineIdx(null)}
          onCancel={() => setAptitudeInlineIdx(null)}
          totalSteps={1}
          currentStep={1}
        />
      )}

      {/* ── Main dialog ── */}
      {!showSuccess && topicQueueIdx === null && !manualPopup && (
        <Dialog open onOpenChange={(o) => !o && onClose()}>
          <DialogContent className="max-w-[980px] max-h-[92vh] overflow-y-auto p-0 gap-0">

            {/* Sticky header */}
            <div className="sticky top-0 z-10 bg-background/95 backdrop-blur border-b px-6 py-4">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="min-w-0">
                  <h2 className="text-sm font-semibold text-foreground">
                    Schedule Interview — <span className="text-muted-foreground font-normal">{candidate.name}</span>
                  </h2>
                  <p className="text-xs text-muted-foreground mt-0.5">{candidate.candidate_email}</p>
                </div>
                <div className="ml-auto flex items-center gap-3">
                  <Steps step={step} />
                  <button
                    type="button"
                    onClick={onClose}
                    aria-label="Close schedule interview"
                    className={cn(
                      "inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md border transition-colors",
                      "focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2",
                      "bg-white text-emerald-700 border-emerald-200 hover:bg-emerald-50",
                      "dark:bg-emerald-600 dark:text-white dark:border-emerald-500 dark:hover:bg-emerald-500"
                    )}
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>

            <div className="px-6 py-5 space-y-6">

              {/* Step 1 */}
              {step === 1 && (
                <TestCardsMinimal
                  onSelectTestCard={(cards) => { setCards(cards); setStep(2); }}
                  title="Select Tests & Interview Flows"
                  description={`Choose assessments for ${candidate.name}`}
                  lockInfo={lockInfo}
                />
              )}

              {/* Step 2 */}
              {step === 2 && (
                <div className="grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-6 items-start">

                  {/* ── Left: cards ── */}
                  <div className="space-y-4 min-w-0">

                    {/* Difficulty row */}
                    <div className="flex items-center gap-6 flex-wrap">
                      <div className="flex items-center gap-3">
                        <Label className="text-xs font-medium text-muted-foreground whitespace-nowrap">Difficulty</Label>
                        <Select value={difficulty} onValueChange={setDiff}>
                          <SelectTrigger className="w-36 h-8 text-sm">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {difficulties.map(d => (
                              <SelectItem key={d} value={d}>{d[0].toUpperCase()+d.slice(1)}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <span className="text-xs text-muted-foreground">
                        {sortedTests.length} test{sortedTests.length!==1?"s":""} selected
                        {sortedTests.length > 0 && ` · ${sortedTests.reduce((s,tt)=>s+getTotalMinutes(tt),0)} min total`}
                      </span>
                    </div>

                    {/* Test cards */}
                    <div className="space-y-2.5">
                      {sortedTests.map((testType, idx) => {
                        const isRT      = testType.startsWith("/realtime-");
                        const needLang  = containsCoding(testType);
                        const saved     = topicsPerCard[idx] || {};
                        const savedCodingQs = codingQuestionsPerCard[idx] ?? [];
                        const locked    = isTestLocked(testType);

                        return (
                          <Card key={`${testType}-${idx}`}
                            className={cn(
                              "overflow-hidden transition-colors",
                              locked
                                ? "border-destructive/40 bg-destructive/[0.03]"
                                : "border-border/60 hover:border-border"
                            )}>

                            {/* Card header */}
                            <div className="flex items-start justify-between gap-3 px-4 py-3 bg-muted/20 border-b border-border/40">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className="text-sm font-semibold text-foreground">{displayName(testType)}</span>
                                  {isRT && (
                                    <Badge variant="outline" className="text-[10px] px-1.5 py-0 bg-amber-50 dark:bg-amber-950/20 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-700">
                                      Live
                                    </Badge>
                                  )}
                                  {locked && (
                                    <Badge variant="outline" className="text-[10px] px-1.5 py-0 gap-1 bg-destructive/10 text-destructive border-destructive/30">
                                      <Lock className="w-2.5 h-2.5" />
                                      Not in {ent?.plan_name || "your"} plan
                                    </Badge>
                                  )}
                                </div>
                                <FlowPills testType={testType} testCosts={testCosts} />

                                {/* Inline topic editor buttons */}
                                <QuickTopicEditor
                                  cardIdx={idx}
                                  testType={testType}
                                  topicsPerCard={topicsPerCard}
                                  codingQuestionsPerCard={codingQuestionsPerCard}
                                  onOpenPopup={openManualPopup}
                                />

                                {/* Aptitude question source (drives 1-vs-2 credit cost) */}
                                {testType === "/aptitude" && (
                                  <div className="flex items-center gap-1.5 mt-2 pt-2 border-t border-border/30">
                                    <Sparkles className="w-3 h-3 text-muted-foreground/60 shrink-0" />
                                    <span className="text-[11px] text-muted-foreground">Question source:</span>
                                    <button
                                      type="button"
                                      onClick={() => setAptitudeInlineIdx(idx)}
                                      className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-md border border-dashed border-border/60 text-muted-foreground hover:border-primary hover:text-primary transition-colors"
                                    >
                                      <Pencil className="w-2.5 h-2.5" />
                                      {(() => {
                                        const mode = aptitudeConfigPerCard?.[idx]?.config?.source_mode;
                                        if (mode === "template") return "Your template · 1 cr/candidate";
                                        if (mode === "mixed") return "Template + AI · 2 cr/candidate";
                                        if (mode === "ai") return "AI-generated · 2 cr/candidate";
                                        return "Configure (AI 2 cr · your template 1 cr)";
                                      })()}
                                    </button>
                                  </div>
                                )}

                                {/* Topic/question chips preview */}
                                {(saved.screening?.length > 0) && (
                                  <TopicChips screeningTopics={saved.screening || []} codingTopics={[]} />
                                )}
                                {savedCodingQs.length > 0 && (
                                  <div className="flex flex-wrap gap-1 mt-2 pt-2 border-t border-border/30">
                                    {savedCodingQs.map((q, qi) => (
                                      <span key={qi} className="inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded-md border bg-violet-50 dark:bg-violet-950/30 text-violet-700 dark:text-violet-300 border-violet-200 dark:border-violet-700">
                                        <Code2 className="w-2.5 h-2.5 shrink-0" />
                                        {q.title || q.description.slice(0, 32) + (q.description.length > 32 ? "…" : "")}
                                      </span>
                                    ))}
                                  </div>
                                )}
                              </div>
                              <DurationCreditBadge testType={testType} testCosts={testCosts} cr={cardCr(testType, idx)} />
                            </div>

                            {/* Card controls */}
                            <div className="px-4 py-3 flex gap-3 flex-wrap">
                              <div className="flex-1 min-w-[220px] space-y-1">
                                <Label className="text-[11px] text-muted-foreground font-medium">Date & Time</Label>
                                <DateTimePicker
                                  value={testTimes[idx] ?? ""}
                                  onChange={(v) => handleTimeChange(v, idx)}
                                  placeholder="Select date and time"
                                  minDate={new Date().toISOString().split("T")[0]}
                                  className="h-8 text-sm"
                                />
                              </div>

                              {needLang && (
                                <div className="flex-1 min-w-[140px] space-y-1">
                                  <Label className="text-[11px] text-muted-foreground font-medium">Language</Label>
                                  <Select value={languages[idx]??""} onValueChange={v => setLangs(p=>({...p,[idx]:v}))}>
                                    <SelectTrigger className="h-8 text-sm">
                                      <SelectValue placeholder="Select…" />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {languageOptions.map(l => (
                                        /* Values are already canonical /
                                           display-ready ("C++", "NodeJS",
                                           "JavaScript") so no in-render
                                           transformation needed. The
                                           older `l[0].toUpperCase()+l.slice(1)`
                                           was a leftover from when values
                                           were lowercase ("cpp" → "Cpp"). */
                                        <SelectItem key={l} value={l}>{l}</SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                </div>
                              )}

                              <div className="flex-1 min-w-[160px] space-y-1">
                                <Label className="text-[11px] text-muted-foreground font-medium">Platform</Label>
                                <Select value={platforms[idx]??"web"} onValueChange={v => setPlatforms(p=>({...p,[idx]:v}))}>
                                  <SelectTrigger className="h-8 text-sm">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="web">Web browser</SelectItem>
                                    <SelectItem value="desktop">Desktop app</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>

                              {testType === "/realtime-pnb" && (
                                <div className="w-full space-y-1">
                                  <Label className="text-[11px] text-muted-foreground font-medium">Question set</Label>
                                  <Select
                                    value={pnbTemplateByIdx[idx] ? String(pnbTemplateByIdx[idx]) : "__ai__"}
                                    onValueChange={v => setPnbTemplateByIdx(p => ({ ...p, [idx]: v === "__ai__" ? "" : v }))}
                                  >
                                    <SelectTrigger className="h-8 text-sm">
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="__ai__">AI-generated from job description (default)</SelectItem>
                                      {pnbTemplates.map(t => (
                                        <SelectItem key={t.id} value={String(t.id)}>
                                          {t.name}{t.question_count ? ` (${t.question_count})` : ""}
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                  <p className="text-[10px] text-muted-foreground">
                                    {pnbTemplatesLoaded && pnbTemplates.length === 0
                                      ? "No saved sets yet — create one in Settings → P&B Builder."
                                      : "Sana asks your chosen questions as the backbone, with light follow-ups."}
                                  </p>
                                </div>
                              )}
                            </div>
                          </Card>
                        );
                      })}
                    </div>

                    {/* Topics hint banner */}
                    {sortedTests.some(tt => containsScreening(tt) || containsCoding(tt)) && (
                      <div className="flex items-start gap-2.5 rounded-lg border border-dashed border-border/60 bg-muted/10 px-3.5 py-2.5">
                        <Sparkles className="w-3.5 h-3.5 text-primary/60 shrink-0 mt-0.5" />
                        <div>
                          <p className="text-xs font-medium text-foreground">Customise Questions</p>
                          <p className="text-[11px] text-muted-foreground mt-0.5 leading-snug">
                            Add your own coding problems (with optional I/O examples) or screening topics — or you'll be prompted before scheduling. AI fills in any missing fields.
                          </p>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* ── Right: credit widget + summary ── */}
                  <div className="lg:sticky lg:top-[80px] space-y-4">
                    <CreditWidget
                      balance={balance}
                      testCosts={testCosts}
                      sortedTests={sortedTests}
                      totalCr={totalCr}
                      loading={creditLoading}
                      onRefresh={loadCredits}
                    />

                    {sortedTests.length > 0 && (
                      <div className="rounded-xl border border-border/60 overflow-hidden">
                        <div className="bg-muted/30 border-b border-border/40 px-3.5 py-2">
                          <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-widest">Summary</p>
                        </div>
                        <div className="divide-y divide-border/40">
                          {sortedTests.map((testType, idx) => {
                            const saved = topicsPerCard[idx] || {};
                            const codingQCount = (codingQuestionsPerCard[idx] ?? []).length;
                            const topicCount = (saved.screening?.length||0) + codingQCount;
                            return (
                              <div key={idx} className="px-3.5 py-2.5 space-y-1">
                                <div className="flex items-start justify-between gap-2">
                                  <div className="min-w-0">
                                    <p className="text-xs font-medium text-foreground leading-tight truncate">
                                      {displayName(testType)}
                                    </p>
                                    {testTimes[idx] && (
                                      <p className="text-[10px] text-muted-foreground mt-0.5">{fmtDateTime(testTimes[idx])}</p>
                                    )}
                                  </div>
                                  <Badge className="text-[10px] shrink-0 bg-amber-50 dark:bg-amber-950/20 text-amber-700 dark:text-amber-300 border border-amber-200 dark:border-amber-700">
                                    {fmtCr(cardCr(testType, idx))} cr
                                  </Badge>
                                </div>
                                {(saved.screening?.length||0) > 0 && (
                                  <p className="text-[10px] text-muted-foreground flex items-center gap-1">
                                    <BookOpen className="w-2.5 h-2.5" />{saved.screening.length} screening topic{saved.screening.length!==1?"s":""} configured
                                  </p>
                                )}
                                {codingQCount > 0 && (
                                  <p className="text-[10px] text-violet-600 dark:text-violet-400 flex items-center gap-1">
                                    <Code2 className="w-2.5 h-2.5" />{codingQCount} custom question{codingQCount!==1?"s":""} added
                                  </p>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* ── Footer ── */}
            {step === 2 && (
              <div className="sticky bottom-0 bg-background/95 backdrop-blur border-t px-6 py-3.5">
                {hasLocked && (
                  <div className="mb-3 flex items-start gap-3 rounded-xl border border-amber-300/70 bg-gradient-to-br from-amber-50 via-white to-white px-3 py-2.5 shadow-sm">
                    <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-amber-100 text-amber-600 ring-4 ring-amber-50">
                      <Lock className="w-4 h-4" />
                    </div>
                    <div className="flex-1">
                      <p className="text-xs leading-relaxed text-slate-600">
                        <span className="font-semibold text-slate-900">{lockedSelected.map(displayName).join(", ")}</span>
                        {lockedSelected.length > 1 ? " aren't" : " isn't"} included in your{" "}
                        <strong className="text-slate-800">{ent?.plan_name || "current"}</strong> plan. Go back to remove{" "}
                        {lockedSelected.length > 1 ? "them" : "it"}, or upgrade to unlock.
                      </p>
                      <Button
                        size="sm"
                        className="mt-2 h-7 gap-1.5 px-2.5 text-xs font-semibold"
                        onClick={() => { try { window.location.assign('/hirewithus/billing'); } catch (_) {} }}
                      >
                        <Zap className="w-3.5 h-3.5" />
                        Upgrade plan
                      </Button>
                    </div>
                  </div>
                )}
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <Button variant="outline" size="sm" onClick={() => setStep(1)} className="gap-1.5">
                    <ChevronLeft className="w-3.5 h-3.5" />Change Tests
                  </Button>
                  <div className="flex items-center gap-3">
                    {!readyToSchedule && sortedTests.length > 0 && (
                      <span className="text-xs text-muted-foreground hidden sm:block">
                        {hasLocked     ? "Remove locked tests or upgrade" :
                         !allTimesSet ? "Set all times first" :
                         !allLangsSet ? "Select language for coding tests" :
                         !canAfford   ? "Insufficient credits" : ""}
                      </span>
                    )}
                    <Button
                      size="sm"
                      onClick={handleScheduleClick}
                      disabled={isScheduling || !readyToSchedule}
                      className="gap-1.5 min-w-[140px]"
                      style={{ backgroundColor: getColor("primary"), color: getColor("primaryForeground") }}
                    >
                      {isScheduling
                        ? <><Loader2 className="w-3.5 h-3.5 animate-spin" />Scheduling…</>
                        : <><ChevronRight className="w-3.5 h-3.5" />Schedule All</>
                      }
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      )}

      {/* ── Progress dialog ── */}
      {isScheduling && (
        <Dialog open onOpenChange={() => {}}>
          {/* Deliberately not closable: schedule requests are in flight and
              credits are being charged — hide the X instead of a dead one. */}
          <DialogContent className="max-w-sm" hideClose>
            <DialogHeader>
              <DialogTitle className="text-sm">Scheduling Tests</DialogTitle>
              <DialogDescription className="text-xs">Setting up each assessment…</DialogDescription>
            </DialogHeader>
            <div className="space-y-2 mt-3">
              {sortedTests.map((id, i) => {
                const isCurrent   = progress.current?.id === id;
                const isCompleted = progress.completed?.includes(id);
                const status = isCompleted?"done":isCurrent?"running":"pending";
                return (
                  <div key={i} className={cn(
                    "flex items-center gap-3 rounded-lg p-2.5 border text-sm transition-colors",
                    status==="done"    && "bg-emerald-50 dark:bg-emerald-950/20 border-emerald-200 dark:border-emerald-800",
                    status==="running" && "bg-amber-50 dark:bg-amber-950/20 border-amber-200 dark:border-amber-800",
                    status==="pending" && "bg-muted/40 border-border/40",
                  )}>
                    {status==="done"    && <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />}
                    {status==="running" && <Loader2 className="w-4 h-4 text-amber-600 animate-spin shrink-0" />}
                    {status==="pending" && <span className="w-4 h-4 flex items-center justify-center text-[10px] text-muted-foreground font-bold shrink-0">{i+1}</span>}
                    <div>
                      <p className="font-medium text-xs">{displayName(id)}</p>
                      <p className="text-[10px] text-muted-foreground">
                        {status==="done"?"Scheduled ✓":status==="running"?"Scheduling…":"Waiting"}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* ── Success dialog ── */}
      {showSuccess && (
        <Dialog open onOpenChange={onClose}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle>Tests Scheduled 🎉</DialogTitle>
              <DialogDescription>Successfully scheduled for {candidate.name}</DialogDescription>
            </DialogHeader>
            <div className="space-y-2.5 mt-3">
              {created.map((c) => (
                <div key={c.testId} className="rounded-lg bg-muted/40 border px-3 py-2.5 space-y-1.5">
                  <div className="flex items-center justify-between">
                    <p className="text-xs font-semibold">{displayName(c.cardId)}</p>
                    <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                  </div>
                  <p className="text-[10px] text-muted-foreground">{fmtDateTime(c.scheduledTime)}</p>
                  {(c.screeningTopics?.length > 0 || c.codingTopics?.length > 0) && (
                    <div className="flex flex-wrap gap-1 pt-1 border-t border-border/30">
                      {c.screeningTopics?.map((t, i) => (
                        <span key={i} className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded border bg-sky-50 dark:bg-sky-950/30 text-sky-700 dark:text-sky-300 border-sky-200 dark:border-sky-700">
                          <Search className="w-2 h-2" />{t}
                        </span>
                      ))}
                      {c.codingTopics?.map((t, i) => (
                        <span key={i} className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded border bg-violet-50 dark:bg-violet-950/30 text-violet-700 dark:text-violet-300 border-violet-200 dark:border-violet-700">
                          <Code2 className="w-2 h-2" />{t}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              Confirmation email sent to {candidate.candidate_email}
            </p>
            <DialogFooter>
              <Button size="sm" onClick={() => { setSuccess(false); onClose(); window.location.reload(true); }}
                style={{ backgroundColor: getColor("primary"), color: getColor("primaryForeground") }}>
                Done
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}
